import { BehaviorSubject, map } from "rxjs";
import { keyValueStorage } from "../../common/logic/KeyValueStore";
import { Profile } from "../model/Profile";
import { doApiGet } from "../../api/Requests";

var inMemoryAccessToken: string | null = null;

interface AuthenticationState {
    isAuthenticated: boolean,
    profile: Profile | null,
    csrfToken: string | null,
}

class AuthenticationStateHandler {
    private authenticationStateSubject = new BehaviorSubject<AuthenticationState>(
        this.readAuthenticationStateFromStorage()
    );

    get isAuthenticatedObservable() {
        return this.authenticationStateSubject.asObservable()
            .pipe(map(s => s.isAuthenticated))
    }

    get accessToken(): string | null {
        return inMemoryAccessToken;
    }

    get isAuthenticated(): boolean {
        return this.authenticationStateSubject.value.isAuthenticated
    }

    get profileObservable() {
        return this.authenticationStateSubject.asObservable()
            .pipe(map(s => s.profile))
    }

    get profile(): Profile | null {
        return this.authenticationStateSubject.value.profile
    }

    get csrfToken(): string | null {
        return this.authenticationStateSubject.value.csrfToken
    }

    setNotAuthenticated() {
        let authenticationState = {
            isAuthenticated: false,
            profile: null,
            accessToken: null,
            csrfToken: null,
        } as AuthenticationState

        this.authenticationStateSubject.next(authenticationState);
        this.writeAuthenticationStateToStorage(authenticationState);
    }

    setAuthenticationTokens(accessToken: string | null, csrfToken: string | null) {
        let currentState = this.readAuthenticationStateFromStorage();
        let newState = {
            ...currentState,
            csrfToken
        }

        inMemoryAccessToken = accessToken;

        this.authenticationStateSubject.next(newState);
        this.writeAuthenticationStateToStorage(newState);
    }

    setAuthenticated(
        profile: Profile,
        accessToken: string | null,
        csrfToken: string | null,
    ) {
        inMemoryAccessToken = accessToken;

        let authenticationState = {
            isAuthenticated: true,
            profile,
            csrfToken
        } as AuthenticationState

        this.authenticationStateSubject.next(authenticationState);
        this.writeAuthenticationStateToStorage(authenticationState);
    }

    async refreshProfileAndSetAuthenticated(
        accessToken: string | null,
        csrfToken: string | null,
    ): Promise<boolean> {
        const meResult = await doApiGet<Profile>(
            "/api/me",
            false,
            true,
            { accessToken: accessToken ?? undefined }
        )
        const meResultData = meResult.getResultOrNull()

        if (meResult.success() && meResultData != null) {
            authenticationStateHandler.setAuthenticated(
                meResultData,
                accessToken,
                csrfToken,
            )
            return true
        }

        return false
    }

    private readAuthenticationStateFromStorage(): AuthenticationState {
        return keyValueStorage.getObject<AuthenticationState>("authenticationState", {
            profile: null,
            isAuthenticated: false,
            csrfToken: null,
        });
    }

    private writeAuthenticationStateToStorage(state: AuthenticationState) {
        keyValueStorage.setObject("authenticationState", state)
    }
}

export const authenticationStateHandler = new AuthenticationStateHandler();
